import { useState, useRef } from "react";
import { useAuth, API } from "../App";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { User, Mail, Phone, MapPin, Calendar, CreditCard, Shield, ArrowRight, CheckCircle, AlertCircle, Loader2, Bell, BellOff, BellRing, Camera, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { usePushNotifications } from "../hooks/usePushNotifications";

export default function ProfilePage() {
  const { user, token, logout, refreshUser } = useAuth();
  const [sendingVerification, setSendingVerification] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const { 
    isSupported, 
    isSubscribed, 
    isLoading: pushLoading, 
    permission, 
    vapidConfigured,
    subscribe, 
    unsubscribe 
  } = usePushNotifications();

  if (!user) return null;

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be less than 2MB");
      return;
    }

    setUploadingImage(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64 = e.target.result;
          
          await axios.post(`${API}/profile/upload-image`, {
            image_data: base64,
            content_type: file.type
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          toast.success("Profile picture updated!");
          refreshUser(); // Refresh user data to show new image
        } catch (error) {
          toast.error(error.response?.data?.detail || "Failed to upload image");
        } finally {
          setUploadingImage(false);
        }
      };
      reader.onerror = () => {
        toast.error("Failed to read image file");
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Failed to process image");
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!confirm("Are you sure you want to remove your profile picture?")) return;
    
    try {
      await axios.delete(`${API}/profile/image`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile picture removed");
      refreshUser();
    } catch (error) {
      toast.error("Failed to remove profile picture");
    }
  };

  const sendVerificationEmail = async () => {
    setSendingVerification(true);
    try {
      const response = await axios.post(`${API}/auth/send-verification`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Verification email sent! Check your inbox.");
      // For demo, show the token in console
      if (response.data.demo_token) {
        if (process.env.NODE_ENV === "development") {
          console.log("Demo verification link:", `${window.location.origin}/verify-email/${response.data.demo_token}`);
        }
        toast.info("Demo: Check browser console for verification link");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send verification email");
    } finally {
      setSendingVerification(false);
    }
  };

  const handlePushToggle = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) {
        toast.success("Unsubscribed from notifications");
      } else {
        toast.error("Failed to unsubscribe");
      }
    } else {
      const success = await subscribe();
      if (success) {
        toast.success("Subscribed to notifications! You'll receive match day alerts.");
      } else if (permission === 'denied') {
        toast.error("Notifications blocked. Please enable them in your browser settings.");
      } else {
        toast.error("Failed to subscribe to notifications");
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="profile-page">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex items-center gap-6 mb-8">
          <div className="relative group">
            {user.profile_image_url ? (
              <img 
                src={user.profile_image_url} 
                alt={user.full_name}
                className="w-24 h-24 rounded-full object-cover border-4 border-[#7C2C3B]"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-[#7C2C3B] flex items-center justify-center">
                <User className="w-12 h-12 text-white" />
              </div>
            )}
            
            {/* Upload/Change overlay */}
            <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                 onClick={() => fileInputRef.current?.click()}>
              {uploadingImage ? (
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              ) : (
                <Camera className="w-8 h-8 text-white" />
              )}
            </div>
            
            {/* Hidden file input */}
            <input 
              ref={fileInputRef}
              type="file" 
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              data-testid="profile-image-input"
            />
            
            {/* Delete button if image exists */}
            {user.profile_image_url && (
              <button
                onClick={handleDeleteImage}
                className="absolute -bottom-1 -right-1 w-7 h-7 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white shadow-lg transition-colors"
                title="Remove profile picture"
                data-testid="delete-profile-image-btn"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
              {user.full_name.toUpperCase()}
            </h1>
            <p className="text-slate-500">Member since {new Date(user.created_at).toLocaleDateString('en-GB', {
              month: 'long', year: 'numeric'
            })}</p>
            <div className="flex gap-2 mt-2">
              {user.is_subscribed && (
                <span className="badge-claret">Subscriber</span>
              )}
              {user.is_admin && (
                <span className="badge-blue">Admin</span>
              )}
              {user.email_verified && (
                <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded">Verified</span>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-2">Hover over image to change photo</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Account Details */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              ACCOUNT DETAILS
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-400" />
                <div className="flex-1">
                  <div className="text-xs text-slate-500 uppercase">Email</div>
                  <div className="text-[#0F172A] flex items-center gap-2">
                    {user.email}
                    {user.email_verified ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                    )}
                  </div>
                </div>
              </div>
              
              {/* Email Verification */}
              {!user.email_verified && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-700 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    Email not verified
                  </div>
                  <Button
                    size="sm"
                    onClick={sendVerificationEmail}
                    disabled={sendingVerification}
                    className="mt-2 bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="send-verification-btn"
                  >
                    {sendingVerification ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      "Send Verification Email"
                    )}
                  </Button>
                </div>
              )}
              
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-slate-400" />
                <div>
                  <div className="text-xs text-slate-500 uppercase">Telephone</div>
                  <div className="text-[#0F172A]">{user.telephone}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-slate-400" />
                <div>
                  <div className="text-xs text-slate-500 uppercase">Postcode</div>
                  <div className="text-[#0F172A]">{user.postcode}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-400" />
                <div>
                  <div className="text-xs text-slate-500 uppercase">Member Since</div>
                  <div className="text-[#0F172A]">{new Date(user.created_at).toLocaleDateString('en-GB')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              SUBSCRIPTION
            </h2>
            
            {user.is_subscribed ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                  <CreditCard className="w-8 h-8 text-green-600" />
                  <div>
                    <div className="font-semibold text-green-800">Active Subscription</div>
                    <div className="text-sm text-green-600">
                      Valid until {new Date(user.subscription_end).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'long', year: 'numeric'
                      })}
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-slate-500">
                  Your £2.50/month subscription gives you full access to all All WHUFC features including the fan forum.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                  <CreditCard className="w-8 h-8 text-slate-400" />
                  <div>
                    <div className="font-semibold text-slate-700">No Active Subscription</div>
                    <div className="text-sm text-slate-500">Subscribe to unlock all features</div>
                  </div>
                </div>
                
                <Link to="/subscription">
                  <Button className="w-full bg-[#7C2C3B] hover:bg-[#63232F]" data-testid="profile-subscribe-btn">
                    SUBSCRIBE - £2.50/MONTH <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Push Notifications */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 md:col-span-2">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              <Bell className="w-5 h-5 inline mr-2 text-[#F8D742]" />
              PUSH NOTIFICATIONS
            </h2>
            
            {!isSupported ? (
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                <BellOff className="w-8 h-8 text-slate-400" />
                <div>
                  <div className="font-semibold text-slate-700">Not Supported</div>
                  <div className="text-sm text-slate-500">Your browser doesn't support push notifications</div>
                </div>
              </div>
            ) : !vapidConfigured ? (
              <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg">
                <AlertCircle className="w-8 h-8 text-amber-600" />
                <div>
                  <div className="font-semibold text-amber-700">Not Available</div>
                  <div className="text-sm text-amber-600">Push notifications are not configured yet</div>
                </div>
              </div>
            ) : pushLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-[#7C2C3B]" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className={`flex items-center gap-3 p-4 rounded-lg ${isSubscribed ? 'bg-green-50' : 'bg-slate-50'}`}>
                  {isSubscribed ? (
                    <BellRing className="w-8 h-8 text-green-600" />
                  ) : (
                    <BellOff className="w-8 h-8 text-slate-400" />
                  )}
                  <div className="flex-1">
                    <div className={`font-semibold ${isSubscribed ? 'text-green-800' : 'text-slate-700'}`}>
                      {isSubscribed ? 'Notifications Enabled' : 'Notifications Disabled'}
                    </div>
                    <div className={`text-sm ${isSubscribed ? 'text-green-600' : 'text-slate-500'}`}>
                      {isSubscribed 
                        ? "You'll receive match day alerts and club updates" 
                        : "Enable to get match day alerts and club updates"}
                    </div>
                  </div>
                </div>
                
                {permission === 'denied' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-700">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      Notifications are blocked. Please enable them in your browser settings.
                    </p>
                  </div>
                )}
                
                <Button 
                  onClick={handlePushToggle}
                  className={isSubscribed 
                    ? "w-full bg-slate-600 hover:bg-slate-700" 
                    : "w-full bg-[#7C2C3B] hover:bg-[#63232F]"
                  }
                  disabled={permission === 'denied'}
                  data-testid="push-toggle-btn"
                >
                  {isSubscribed ? (
                    <><BellOff className="w-4 h-4 mr-2" /> DISABLE NOTIFICATIONS</>
                  ) : (
                    <><BellRing className="w-4 h-4 mr-2" /> ENABLE NOTIFICATIONS</>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Admin Access */}
          {user.is_admin && (
            <div className="bg-white rounded-xl p-6 border border-slate-200 md:col-span-2">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                <Shield className="w-5 h-5 inline mr-2 text-[#2BAEE5]" />
                ADMIN ACCESS
              </h2>
              
              <p className="text-slate-600 mb-4">
                You have admin privileges. Access the admin panel to manage content, users, and more.
              </p>
              
              <Link to="/admin">
                <Button className="bg-[#2BAEE5] hover:bg-[#228BB7]" data-testid="admin-panel-btn">
                  OPEN ADMIN PANEL <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Logout */}
        <div className="mt-8 text-center">
          <Button 
            variant="outline" 
            onClick={logout}
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid="logout-btn"
          >
            Sign Out
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
}
